import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentCompany } from "./useCurrentCompany";

export type NavTabId =
  | "work-orders"
  | "inventory"
  | "search"
  | "scanner"
  | "store"
  | "profile"
  | "admin";

export const ALL_NAV_TABS: NavTabId[] = [
  "work-orders",
  "inventory",
  "search",
  "scanner",
  "store",
  "profile",
  "admin",
];

/**
 * Per-tenant bottom navigation visibility.
 * A tab is enabled when no override row exists or the row has enabled=true.
 * `profile` is always enabled (users need to sign out / switch language).
 */
export function useCompanyNavTabs() {
  const { id: companyId } = useCurrentCompany();

  const { data, isLoading } = useQuery({
    queryKey: ["company_nav_tabs", companyId],
    enabled: !!companyId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("company_nav_tabs")
        .select("tab_key, enabled")
        .eq("company_id", companyId);
      const map = new Map<string, boolean>();
      for (const r of (data ?? []) as { tab_key: string; enabled: boolean }[]) {
        map.set(r.tab_key, r.enabled);
      }
      return map;
    },
  });

  // Ready only once we know the company AND have its rows; until then, tabs
  // that can be turned off stay hidden so they never flash in the menu.
  const ready = !!companyId && data !== undefined;

  const isTabEnabled = (id: NavTabId) => {
    // Profile (sign out / switch language) and Admin are always visible;
    // Admin rendering is still gated to admin/superadmin roles by the
    // bottom tab bar, sidebar, and AdminRoute.
    if (id === "profile" || id === "admin") return true;
    if (!ready) return false;
    const v = data.get(id);
    return v === undefined ? true : v;
  };

  const enabledTabs = new Set<NavTabId>(ALL_NAV_TABS.filter(isTabEnabled));

  return { enabledTabs, isTabEnabled, loading: isLoading || !ready, ready };
}
